import React, { useEffect, useMemo, useState } from "react";
import AppFullscreenModal from "../AppFullscreenModal";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { resolveUploadedFileUrl } from "../../helpers/resolveMediaUrl";
import ActionChips from "./ActionChips";
import ConfirmationModal from "../ConfirmationModal";
import MediasModal from "./MediasModal";

function FieldLabel({ children }) {
	return (
		<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
			{children}
		</label>
	);
}

function IconTrash() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconPlus() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function IconLink() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07L11.7 5.24" />
			<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.77-1.76" />
		</svg>
	);
}

function IconMedia() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
			<circle cx="8.5" cy="8.5" r="1.5" />
			<path d="m21 15-5-5L5 21" />
		</svg>
	);
}

function EmptyState({ title, description }) {
	return (
		<div className="rounded-2xl border border-dashed border-default-300 bg-default-50 px-6 py-10 text-center dark:border-default-700 dark:bg-default-900">
			<p className="mb-1 text-base font-semibold text-default-900 dark:text-default-100">
				{title}
			</p>
			<p className="m-0 text-sm text-default-500 dark:text-default-400">
				{description}
			</p>
		</div>
	);
}

function NodeBadge({ children, solid = false }) {
	return (
		<span
			className={`inline-flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
				solid
					? "bg-primary text-white"
					: "border border-primary/30 bg-primary/10 text-primary"
			}`}
		>
			{children}
		</span>
	);
}

function ChatbotModal({
	open,
	onClose,
	selectedChatbot,
	setSelectedChatbot,
	chatbots,
	setChatbots,
}) {
	const [creatingLoading, setCreatingLoading] = useState(false);
	const [creatingNode, setCreatingNode] = useState(false);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [confirmationFunction, setConfirmationFunction] = useState(null);
	const [confirmationTitle, setConfirmationTitle] = useState("");
	const [mediasModalOpen, setMediasModalOpen] = useState(false);
	const [selectedNode, setSelectedNode] = useState(null);
	const [chatbotName, setChatbotName] = useState("");

	const nodes = useMemo(
		() => (Array.isArray(selectedChatbot?.nodes) ? selectedChatbot.nodes : []),
		[selectedChatbot]
	);

	const selectedNodeMedias = useMemo(
		() => nodes.find(node => node.id === selectedNode)?.nodeMedias || [],
		[nodes, selectedNode]
	);

	useEffect(() => {
		setChatbotName(selectedChatbot?.name || "");
	}, [selectedChatbot?.id, selectedChatbot?.name, open]);

	useEffect(() => {
		if (!confirmationOpen) {
			setConfirmationFunction(null);
			setConfirmationTitle("");
		}
	}, [confirmationOpen]);

	const handleClose = () => {
		onClose();
		setMediasModalOpen(false);
		setSelectedNode(null);
	};

	const updateNodes = updater => {
		setSelectedChatbot(prevState => {
			if (!prevState) return prevState;
			return {
				...prevState,
				nodes: updater(Array.isArray(prevState.nodes) ? prevState.nodes : []),
			};
		});
	};

	const createNode = async () => {
		if (!selectedChatbot?.id) return null;

		try {
			setCreatingNode(true);
			const { data } = await api.post("/chatbot/node", {
				header: `Nó ${nodes.length + 1 || 1}`,
				chatbotId: selectedChatbot.id,
			});
			return data;
		} catch (err) {
			toastError(err);
			return null;
		} finally {
			setCreatingNode(false);
		}
	};

	const createOption = async nodeId => {
		if (creatingLoading) return null;

		try {
			setCreatingLoading(true);
			const currentNode = nodes.find(node => node.id === nodeId);
			const { data } = await api.post("/chatbot/option", {
				text: `Opção ${(currentNode?.options?.length || 0) + 1}`,
				nodeId,
				toNodeId: null,
				actionId: null,
				chatbotId:selectedChatbot.id,
			});
			return data;
		} catch (err) {
			toastError(err);
			return null;
		} finally {
			setCreatingLoading(false);
		}
	};

	const handleDeleteNode = async nodeId => {
		try {
			await api.delete(`/chatbot/node/${nodeId}`);
			updateNodes(currentNodes => currentNodes.filter(node => node.id !== nodeId));
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteOption = async optionId => {
		try {
			await api.delete(`/chatbot/option/${optionId}`);
			updateNodes(currentNodes =>
				currentNodes.map(node => ({
					...node,
					options: (node.options || []).filter(option => option.id !== optionId),
				}))
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleUpdateNode = async (nodeId, data) => {
		try {
			const { data: updatedNode } = await api.put(`/chatbot/node/${nodeId}`, data);

			updateNodes(currentNodes =>
				currentNodes.map(node =>
					node.id === nodeId ? { ...node, ...updatedNode, ...data } : node
				)
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleAddNodeUrl = async (event, nodeId) => {
		if (!event.target.files?.length) {
			return;
		}

		try {
			const file = event.target.files[0];
			const formData = new FormData();
			formData.append("file", file);

			const response = await api.post("/chatbot/node-upload-image", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			const url = resolveUploadedFileUrl(file, response);
			const { data: nodeMedia } = await api.post(`/chatbot/node-media/${nodeId}`, {
				url,
			});

			updateNodes(currentNodes =>
				currentNodes.map(node =>
					node.id === nodeId
						? {
								...node,
								nodeMedias: [...(node.nodeMedias || []), nodeMedia],
							}
						: node
				)
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteNodeUrl = async (nodeId, mediaId) => {
		try {
			await api.delete(`/chatbot/node-media/${mediaId}`);

			updateNodes(currentNodes =>
				currentNodes.map(node =>
					node.id === nodeId
						? {
								...node,
								nodeMedias: (node.nodeMedias || []).filter(media => media.id !== mediaId),
							}
						: node
				)
			);
		} catch (err) {
			toastError(err);
		}
	};

	const removeNodeUrl = async nodeId => {
		try {
			await api.put(`/chatbot/node/${nodeId}`, { url: null });

			updateNodes(currentNodes =>
				currentNodes.map(node => (node.id === nodeId ? { ...node, url: null } : node))
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleUpdateChatbot = async name => {
		if (!selectedChatbot?.id || !name?.trim()) return;

		try {
			const { data: chatbot } = await api.put(`/chatbot/${selectedChatbot.id}`, {
				name,
			});

			setChatbotName(chatbot.name);
			setSelectedChatbot(prevState =>
				prevState ? { ...prevState, name: chatbot.name } : prevState
			);
			setChatbots(
				chatbots.map(item =>
					item.id === chatbot.id ? { ...item, name: chatbot.name } : item
				)
			);
		} catch (err) {
			toastError(err);
		}
	};

	const updateOption = async data => {
		const { text, optionId, toNodeId, disabled } = data;
		const body = {};

		if (toNodeId !== undefined) {
			body.toNodeId = toNodeId === "empty" ? null : toNodeId;
		}

		if (text !== undefined) {
			body.text = text;
		}

		if (disabled !== undefined) {
			body.disabled = disabled;
		}

		try {
			const { data: option } = await api.put(`/chatbot/option/${optionId}`, body);
			return option;
		} catch (err) {
			toastError(err);
			return null;
		}
	};

	const handleOptionUpdate = (nodeId, optionId, updatedOption) => {
		if (!updatedOption) return;

		updateNodes(currentNodes =>
			currentNodes.map(node => {
				if (node.id !== nodeId) return node;

				return {
					...node,
					options: (node.options || []).map(option =>
						option.id === optionId ? { ...option, ...updatedOption } : option
					),
				};
			})
		);
	};

	const handleAddOption = async nodeId => {
		const newOption = await createOption(nodeId);
		if (!newOption) return;

		updateNodes(currentNodes =>
			currentNodes.map(node => {
				if (node.id !== nodeId) return node;

				return {
					...node,
					options: [...(node.options || []), newOption],
				};
			})
		);
	};

	const handleAddNode = async () => {
		const newNode = await createNode();
		if (!newNode) return;

		updateNodes(currentNodes => [...currentNodes, newNode]);
	};

	return (
		<>
			<AppFullscreenModal
				open={open}
				onClose={handleClose}
				title={selectedChatbot ? "Editar chatbot" : "Adicionar chatbot"}
				subtitle="Monte o fluxo com nós, opções, ações e mídias de forma visual."
				maxWidth={1280}
				footer={
					<button type="button" className="btn bg-primary text-white" onClick={handleClose}>
						Fechar
					</button>
				}
			>
				<div className="space-y-6">
					<section className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
						<div>
							<FieldLabel>Nome do chatbot</FieldLabel>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
								<input
									type="text"
									value={chatbotName}
									onChange={event => setChatbotName(event.target.value)}
									onBlur={event => handleUpdateChatbot(event.target.value)}
									className="form-input w-full"
									placeholder="Ex.: Recepção, Pré-vendas..."
								/>

								<div className="flex flex-wrap items-start gap-3 md:justify-end">
									<button
										type="button"
										className="btn btn-sub-gray"
										onClick={handleAddNode}
										disabled={creatingNode}
									>
										<IconPlus />
										{creatingNode ? "Adicionando..." : "Adicionar nó"}
									</button>
								</div>
							</div>
							<p className="mt-2 mb-0 text-xs text-default-500 dark:text-default-400">
								O nome é salvo automaticamente ao sair do campo.
							</p>
						</div>
					</section>

					<section className="space-y-4">
						{nodes.length === 0 ? (
							<EmptyState
								title="Nenhum nó cadastrado"
								description="Adicione o primeiro nó para começar a montar o fluxo do chatbot."
							/>
						) : (
							nodes.map((node, nodeIndex) => (
								<article
									key={node.id}
									className="rounded-2xl border border-default-200 bg-white shadow-sm dark:border-default-700 dark:bg-default-950"
								>
									<div className="flex flex-col gap-4 border-b border-default-200 p-4 dark:border-default-700 xl:flex-row">
										<div className="flex items-center gap-3 xl:w-52">
											<NodeBadge solid>{nodeIndex + 1}</NodeBadge>
											<div>
												<p className="mb-1 text-sm font-semibold text-default-900 dark:text-default-100">
													Nó {node.id}
												</p>
												<p className="m-0 text-xs text-default-500 dark:text-default-400">
													{(node.options || []).length} opção(ões)
												</p>
											</div>
										</div>

										<div className="min-w-0 flex-1">
											<FieldLabel>Cabeçalho</FieldLabel>
											<textarea
												key={`${node.id}-${node.header || ""}`}
												defaultValue={node.header || ""}
												onBlur={event =>
													handleUpdateNode(node.id, {
														header: event.target.value,
													})
												}
												className="form-input min-h-[96px] w-full resize-y"
												placeholder="Mensagem principal do nó"
											/>
										</div>

										<div className="flex flex-wrap items-start gap-2 xl:w-64 xl:justify-end">
											<button
												type="button"
												className="btn btn-sub-gray"
												onClick={() => {
													setSelectedNode(node.id);
													setMediasModalOpen(true);
												}}
											>
												<IconMedia />
												Mídias ({(node.nodeMedias || []).length})
											</button>

											{node.url && (
												<>
													<a
														href={node.url}
														target="_blank"
														rel="noreferrer"
														className="btn btn-sub-gray"
													>
														<IconLink />
														Mídia destaque
													</a>
													<button
														type="button"
														className="btn btn-sub-gray"
														onClick={() => removeNodeUrl(node.id)}
													>
														Remover destaque
													</button>
												</>
											)}

											<button
												type="button"
												className="btn border border-danger/20 bg-danger/10 text-danger hover:bg-danger/15"
												onClick={() => {
													setConfirmationFunction(() => () => handleDeleteNode(node.id));
													setConfirmationTitle("Excluir nó?");
													setConfirmationOpen(true);
												}}
											>
												<IconTrash />
												Excluir
											</button>
										</div>
									</div>

									<div className="space-y-3 p-4">
										{(node.options || []).length === 0 ? (
											<EmptyState
												title="Nenhuma opção configurada"
												description="Adicione uma opção para definir a próxima ação ou o próximo nó."
											/>
										) : (
											(node.options || []).map((option, optionIndex) => (
												<div
													key={option.id}
													className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900"
												>
													<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
														<div className="flex items-center gap-3">
															<NodeBadge>{optionIndex + 1}</NodeBadge>
															<div>
																<p className="mb-1 text-sm font-semibold text-default-900 dark:text-default-100">
																	Opção {optionIndex + 1}
																</p>
																<p className="m-0 text-xs text-default-500 dark:text-default-400">
																	Configura a resposta e o próximo passo do fluxo
																</p>
															</div>
														</div>

														<div className="flex flex-wrap items-center gap-3">
															<label className="inline-flex items-center gap-2 text-sm text-default-700 dark:text-default-200">
																<input
																	type="checkbox"
																	className="size-4 rounded border-default-300 text-primary focus:ring-primary"
																	checked={!option.disabled}
																	onChange={async event => {
																		const updatedOption = await updateOption({
																			optionId: option.id,
																			disabled: !event.target.checked,
																		});
																		handleOptionUpdate(node.id, option.id, updatedOption);
																	}}
																/>
																Ativa
															</label>

															<button
																type="button"
																className="btn border border-danger/20 bg-danger/10 text-danger hover:bg-danger/15"
																onClick={() => {
																	setConfirmationFunction(
																		() => () => handleDeleteOption(option.id)
																	);
																	setConfirmationTitle("Excluir opção?");
																	setConfirmationOpen(true);
																}}
															>
																<IconTrash />
																Excluir
															</button>
														</div>
													</div>

													<div className="grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(18rem,1fr)]">
														<div className="space-y-4">
															<div>
																<FieldLabel>Texto da opção</FieldLabel>
																<input
																	key={`${option.id}-${option.text || ""}`}
																	type="text"
																	defaultValue={option.text || ""}
																	onBlur={async event => {
																		const updatedOption = await updateOption({
																			text: event.target.value,
																			optionId: option.id,
																		});
																		handleOptionUpdate(node.id, option.id, updatedOption);
																	}}
																	className="form-input w-full"
																	placeholder="Texto exibido para o usuário"
																/>
															</div>

															<div>
																<FieldLabel>Próximo nó</FieldLabel>
																<select
																	className="form-select w-full"
																	value={option.toNodeId ?? "empty"}
																	onChange={async event => {
																		const updatedOption = await updateOption({
																			toNodeId: event.target.value,
																			optionId: option.id,
																		});
																		handleOptionUpdate(node.id, option.id, updatedOption);
																	}}
																>
																	<option value="empty">Nenhum nó</option>
																	{nodes.map(targetNode => (
																		<option key={targetNode.id} value={targetNode.id}>
																			{`Nó ${targetNode.id} - ${targetNode.header || "Sem cabeçalho"}`}
																		</option>
																	))}
																</select>
															</div>
														</div>

														<ActionChips
															optionActions={option.action}
															nodeId={node.id}
															optionId={option.id}
															selectedChatbot={selectedChatbot}
															setSelectedChatbot={setSelectedChatbot}
														/>
													</div>
												</div>
											))
										)}

										<button
											type="button"
											className="btn btn-sub-gray"
											onClick={() => handleAddOption(node.id)}
											disabled={creatingLoading}
										>
											<IconPlus />
											{creatingLoading ? "Adicionando opção..." : "Adicionar opção"}
										</button>
									</div>
								</article>
							))
						)}
					</section>
				</div>
			</AppFullscreenModal>

			<MediasModal
				medias={selectedNodeMedias}
				open={mediasModalOpen}
				onClose={() => setMediasModalOpen(false)}
				handleAddNodeUrl={handleAddNodeUrl}
				handleDeleteNodeUrl={handleDeleteNodeUrl}
				selectedNode={selectedNode}
			/>

			<ConfirmationModal
				title={confirmationTitle}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={confirmationFunction}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
		</>
	);
}

export default ChatbotModal;
